/**
 * Collector photo panel — the thumbnail grid + upload/delete/move/approve actions
 * + lightbox for a single collector.
 *
 * Extracted from CollectorenTab's inline `CollectorPhotoRow` so the legacy tab and
 * the project Dashboard share one implementation. The panel owns all photo state
 * and actions; it returns plain content (NO `<tr>/<td>`) so each consumer supplies
 * its own wrapper — the legacy tab wraps it in `<tr><td colSpan={7}>`, the dashboard
 * drops it inside an accordion body.
 *
 * Gating is preserved exactly as the legacy tab: the upload tile and move-to-
 * drukattest are UNGATED; delete + approve are gated by `photo_approve`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Collector, PhotoItem } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { compressImage } from '@/utils/imageCompression';
import { isPdfPhoto } from '@/utils/photoFiles';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { uploadPool } from '@/utils/uploadPool';
import { getCollectorMeta } from '@/utils/collectors';
import { describeUploadFailures } from '@/utils/uploadFeedback';
import { GoedkeurenModal } from '@/components/foto/GoedkeurenModal';
import {
  listCollectorPhotos,
  uploadCollectorPhoto,
  deleteCollectorPhoto,
  movePhotoToDrukattest,
  updatePhotoStatus,
  addCollectorToPo,
} from '@/api/tasks';
import type { AddToPoOptions } from '@/api/tasks';

export function CollectorPhotoPanel({
  collector,
  projectName,
  projectId,
  prefetchedPhotos,
  installateur = null,
  collectorenGeleverd = false,
  onMovedToDrukattest,
  onPhotosChanged,
}: {
  collector: Collector;
  projectName: string;
  projectId: number;
  prefetchedPhotos?: PhotoItem[];
  /**
   * Werf-level "Collectoren geleverd" toggle for this project. Informational only — it makes
   * the Goedkeuren modal say the minprijs will be applied. The server re-resolves the flag per
   * collector at bill time and is the authority on whether it actually is.
   */
  collectorenGeleverd?: boolean;
  /**
   * Resolved installateur for this collector: `{id, name}` when an OA is linked,
   * `null` when there is none. Used to branch between the PO-billing path and the
   * plain-approve path inside GoedkeurenModal.
   * Optional — defaults to null so legacy callers (CollectorenTab) keep the plain
   * approve path without any changes at the call site.
   */
  installateur?: { id: number; name: string } | null;
  /** Fired after a photo is moved to the drukattest strip (refresh the strip). */
  onMovedToDrukattest?: () => void;
  /** Fired whenever the photo count changes (initial load, upload, delete, move). */
  onPhotosChanged?: (count: number) => void;
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  // Non-fatal outcome of a successful approve (e.g. the server auto-booked the minprijs
  // because the werf is marked "Collectoren geleverd"). Amber, never red.
  const [approveWarning, setApproveWarning] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Modal open/close for the Goedkeuren confirm flow.
  const [showGoedkeurenModal, setShowGoedkeurenModal] = useState(false);
  // Local approved override: set after a successful approve so the approved indicator
  // reflects immediately without waiting for a store→prop round-trip.
  const [approvedLocal, setApprovedLocal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canApprovePhotos = canFeature('photo_approve');
  const fotoStatus = collector.x_studio_fotos_uitvoering;
  // approvedLocal is set immediately on success; fotoStatus catches the prop-updated case.
  const isApproved = approvedLocal || fotoStatus === 'Fotos_Goedgekeurd';
  const canShowApproveButton =
    canApprovePhotos && fotoStatus === 'Fotos_Geupload' && photos.length > 0 && !approvedLocal;

  const meta = getCollectorMeta(collector, projectName);

  // Report count changes to the parent (e.g. the dashboard count chip) without
  // making the callback a dependency — a fresh inline callback each render would
  // otherwise re-fire on every render. Ref holds the latest; the effect keys only
  // off photos.length so it fires exactly when the count actually changes.
  const onPhotosChangedRef = useRef(onPhotosChanged);
  onPhotosChangedRef.current = onPhotosChanged;
  useEffect(() => {
    if (!loading) onPhotosChangedRef.current?.(photos.length);
  }, [photos.length, loading]);

  /**
   * Called by GoedkeurenModal when installateur is null (plain approve, no PO billing).
   * Calls updatePhotoStatus, reflects approved locally, closes the modal.
   */
  const handleApproveNoOa = useCallback(async () => {
    setApproving(true);
    setApproveError(null);
    try {
      await updatePhotoStatus(collector.id, 'Fotos_Goedgekeurd');
      useProjectViewStore
        .getState()
        .updateCollectorFotoStatus(collector.id, 'Fotos_Goedgekeurd');
      setApprovedLocal(true);
      setShowGoedkeurenModal(false);
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Goedkeuren mislukt');
      setShowGoedkeurenModal(false);
    } finally {
      setApproving(false);
    }
  }, [collector.id]);

  /**
   * Called by GoedkeurenModal on PO-add confirm. Bills the collector onto the PO
   * and flips the status server-side. On success, reflects approved locally.
   * Throws so the modal can catch ApiError and show inline/parent notices.
   *
   * `opts` carries the modal's pricing levers. This panel has none, so the modal omits
   * `geplaatst` — the server then resolves "Collectoren geleverd" itself and books the
   * minprijs, reporting it back as a warning (see api/tasks.ts addCollectorToPo).
   */
  const handleAddToPo = useCallback(async (opts: AddToPoOptions): Promise<void> => {
    const result = await addCollectorToPo(collector.id, opts);
    // Server flips status to Fotos_Goedgekeurd on success — reflect locally.
    useProjectViewStore
      .getState()
      .updateCollectorFotoStatus(collector.id, 'Fotos_Goedgekeurd');
    setApprovedLocal(true);
    setShowGoedkeurenModal(false);
    // Warnings are NOT failures (the collector is billed) — show them amber, not red.
    setApproveWarning(
      result.warnings && result.warnings.length > 0 ? result.warnings.join(' · ') : null,
    );
  }, [collector.id]);

  /**
   * Called by GoedkeurenModal on 409 COLLECTOR_ALREADY_ON_PO.
   * Closed the modal and shows the Dutch message inline.
   */
  const handleAlreadyOnPo = useCallback((message: string) => {
    setShowGoedkeurenModal(false);
    setApproveError(message);
  }, []);

  const loadPhotos = useCallback(async (force = false) => {
    // The prefetched batch is an INITIAL-load shortcut only. After a mutation
    // (upload/delete/move) the caller passes force=true so we refetch the real
    // list from the server — otherwise we'd reset to the stale batch snapshot
    // and the just-uploaded photo would vanish until reload.
    if (prefetchedPhotos && !force) {
      setPhotos(prefetchedPhotos);
      setLoading(false);
      return;
    }
    cancelledRef.current = false;
    setLoading(true);
    setLoadError(null);
    try {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), 10_000),
      );
      const r = await Promise.race([
        listCollectorPhotos(collector.id, meta),
        timeout,
      ]);
      if (!cancelledRef.current) {
        setPhotos(r.photos);
        // Passive auto-correct: if backend says Fotos_Geupload but pill shows Geen_Fotos or falsy
        if (
          r.fotoStatus === 'Fotos_Geupload' &&
          (!collector.x_studio_fotos_uitvoering || collector.x_studio_fotos_uitvoering === 'Geen_Fotos')
        ) {
          useProjectViewStore.getState().updateCollectorFotoStatus(collector.id, r.fotoStatus);
        }
      }
    } catch (err) {
      if (!cancelledRef.current) {
        const isTimeout = err instanceof Error && err.message === 'TIMEOUT';
        setLoadError(isTimeout ? "Foto's laden duurde te lang" : "Foto's laden mislukt");
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collector.id, prefetchedPhotos]);

  useEffect(() => {
    loadPhotos();
    return () => { cancelledRef.current = true; };
  }, [loadPhotos]);

  // Accepts a File[] as well as a FileList: the drop handler filters the dropped
  // files, and rebuilding a FileList from them needs `new DataTransfer()`, which
  // older iPad Safari does not have.
  const handleUpload = async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ done: 0, total: fileArr.length });

    const { failed, failures } = await uploadPool(fileArr, async (file) => {
      const compressed = await compressImage(file);
      const result = await uploadCollectorPhoto(collector.id, compressed, meta);
      setPhotos((prev) => [...prev, {
        key: result.key,
        url: result.url,
        filename: file.name,
        lastModified: new Date().toISOString(),
        size: compressed.size,
      }]);
      setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : null);
    }, 2);

    await loadPhotos(true);
    useProjectViewStore.getState().fetchCollectoren(projectId);
    setUploading(false);
    setUploadProgress(null);

    if (failed > 0) {
      setUploadError(describeUploadFailures(failed, failures));
    }
  };

  // PhotoLightbox is an image zoom/pan viewer — a PDF entry would render as a
  // broken <img> and would also break next/prev. Keep it image-only.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (uploading) return;
    // Not filtered here. `dataTransfer.files` carries everything dropped, and a
    // dropped file now takes exactly the same path as a picked one: an
    // unsupported type is rejected with a message naming what IS allowed,
    // instead of a drop-only "sleep alleen afbeeldingen" that never mentioned
    // PDF and silently skipped the bad file in a mixed drop.
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length === 0) return;
    void handleUpload(dropped);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center">
        {loading && (
          <div className="w-20 h-20 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Foto's laden...</span>
            </div>
          </div>
        )}
        {!loading && loadError && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <span>{loadError}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); loadPhotos(true); }}
              className="text-td-blue hover:underline font-medium"
            >
              Opnieuw proberen
            </button>
          </div>
        )}
        {!loading && !loadError && photos.map((p) => (
          <div key={p.key} className="relative group shrink-0">
            <PhotoGridThumb
              photo={p}
              imgClassName="w-20 h-20 object-cover rounded-lg border border-gray-200 hover:border-td-blue transition-colors"
              onOpen={() => {
                setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key));
                setLightboxOpen(true);
              }}
            />
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (!confirm('Verplaats naar drukattest?')) return;
                try {
                  await movePhotoToDrukattest(collector.id, p.key, meta.blok);
                  setPhotos((prev) => prev.filter((ph) => ph.key !== p.key));
                  onMovedToDrukattest?.();
                } catch (err) {
                  console.error('Move to drukattest failed:', err);
                }
              }}
              className="absolute top-0.5 left-0.5 w-5 h-5 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-white"
              aria-label={`Verplaats ${p.filename} naar drukattest`}
              title="Verplaats naar drukattest"
            >
              <svg className="w-3 h-3 text-td-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 17l9.2-9.2M17 17V7H7" />
              </svg>
            </button>
            {canApprovePhotos && (
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!confirm('Foto verwijderen?')) return;
                  setDeleteError(null);
                  try {
                    const result = await deleteCollectorPhoto(collector.id, p.key);
                    setPhotos((prev) => prev.filter((ph) => ph.key !== p.key));
                    if (result.opDrukChanged) {
                      useProjectViewStore.getState().updateCollectorFotoStatus(collector.id, 'Geen_Fotos');
                    }
                  } catch {
                    setDeleteError('Foto verwijderen mislukt');
                  }
                }}
                className="absolute top-0.5 right-0.5 w-5 h-5 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-50"
                aria-label={`Verwijder ${p.filename}`}
                title="Foto verwijderen"
              >
                <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
        {!loading && !loadError && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            disabled={uploading}
            className={`w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors disabled:opacity-50 ${
              isDragOver ? 'border-td-blue bg-td-blue/5' : 'border-gray-300 hover:border-td-blue'
            }`}
            aria-label="Foto of PDF uploaden of sleep hierheen"
            title="Klik om te uploaden of sleep foto's of PDF hierheen"
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
                  <span className="sr-only">Uploaden...</span>
                </div>
                {uploadProgress && (
                  <span className="text-[10px] text-td-muted mt-1">
                    {uploadProgress.done}/{uploadProgress.total}
                  </span>
                )}
              </>
            ) : (
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          multiple
          onChange={(e) => e.target.files && handleUpload(e.target.files)}
          className="hidden"
          aria-label="Selecteer foto's of PDF-bestanden om te uploaden"
        />
        {canShowApproveButton && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setApproveError(null);
              setApproveWarning(null);
              setShowGoedkeurenModal(true);
            }}
            disabled={approving}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-td-green rounded-lg hover:bg-td-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={`Foto's goedkeuren voor ${collector.name}`}
          >
            {approving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Goedkeuren...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Foto&apos;s goedkeuren
              </>
            )}
          </button>
        )}
        {isApproved && photos.length > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-td-green bg-green-50 border border-td-green/30 rounded-lg">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            Goedgekeurd
          </span>
        )}
      </div>
      {uploadError && (
        <p className="text-xs text-red-500 mt-1">{uploadError}</p>
      )}
      {approveError && (
        <p className="text-xs text-red-500 mt-1">{approveError}</p>
      )}
      {approveWarning && (
        <p className="text-xs text-amber-700 mt-1">{approveWarning}</p>
      )}
      {deleteError && (
        <p className="text-xs text-red-500 mt-1">{deleteError}</p>
      )}
      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => { setLightboxOpen(false); }}
        />
      )}
      {showGoedkeurenModal && (
        <GoedkeurenModal
          collector={{
            id: collector.id,
            name: collector.name,
            systeem: collector.x_studio_collector_systeem || null,
            m2: collector.x_studio_collector_m2,
            kringen: collector.kringen?.map((k) => ({
              kringnummer: k.x_kringnummer,
              system: k.x_system || null,
              legpatroon: k.x_legpatroon || null,
            })),
            installateur,
            geleverdWerf: collectorenGeleverd,
          }}
          onCancel={() => setShowGoedkeurenModal(false)}
          onApproveNoOa={handleApproveNoOa}
          onAddToPo={handleAddToPo}
          onAlreadyOnPo={handleAlreadyOnPo}
        />
      )}
    </>
  );
}
